/**
 * Project Tasks: actual work on one project, optionally one location.
 *
 * Every write loads the project (and its client) first and refuses when the
 * project subtree is archived, exactly like Other Costs / Locations; the
 * database trigger enforces the same rule. Delete = is_active false.
 */
import { supabase } from "../../supabase";
import { assertCan, CAPABILITY } from "../permissions";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { shouldSkipSupabase } from "../guestMode";
import { isOffline } from "../connectivity";
import {
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "../operationalLifecycle";
import { readCachedProjectTasks, rememberProjectTasks } from "./tasksCache";
import { applyPendingTaskStatus, discardPendingTaskStatus, queueTaskStatus } from "./taskStatusQueue";
import { createCatalogTask, loadTaskCatalog } from "./taskCatalog";
import {
  buildManualTask,
  buildTaskFromCatalog,
  findCatalogDuplicate,
  sortTasks,
  TASK_STATUS,
  validateTaskInput,
} from "./taskModel";

/**
 * True when the Tasks tables do not exist in this environment yet (the
 * migration is staging-first). Callers hide Tasks instead of showing errors.
 */
export function isTasksUnavailableError(error) {
  const text = `${error?.code || ""} ${error?.message || ""}`;
  return (
    /PGRST205|42P01/.test(text) ||
    (/(project_tasks|task_catalog)/.test(text) && /(does not exist|schema cache)/i.test(text))
  );
}

const MANAGE_DENIED = "You do not have permission to change this task.";
export const OFFLINE_TASKS_MESSAGE = "Reconnect to change tasks. Your tasks are saved on this device for reading.";

async function requireOrg(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) throw new Error("Organization is required.");
  return organizationId;
}

function requireOnline() {
  if (isOffline()) throw new Error(OFFLINE_TASKS_MESSAGE);
}

async function loadWritableProject(actor, projectId, organizationId) {
  const { data: project, error } = await supabase
    .from("projects")
    .select("project_id, organization_id, status, is_active, client_id")
    .eq("project_id", projectId)
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) throw error;
  if (!project || project.organization_id !== organizationId) {
    throw new Error("Project not found.");
  }

  let client = null;
  if (project.client_id) {
    const { data, error: clientError } = await supabase
      .from("clients")
      .select("client_id, archived_at, client_status, organization_id")
      .eq("client_id", project.client_id)
      .eq("organization_id", organizationId)
      .maybeSingle();
    if (clientError) throw clientError;
    if (!data || data.organization_id !== organizationId) {
      throw new Error(OPERATIONAL_LOCK_USER_MESSAGE);
    }
    client = data;
  }
  assertProjectOperationallyWritable(project, client);
}

async function loadTaskRow(actor, taskId, organizationId) {
  const { data, error } = await supabase
    .from("project_tasks")
    .select("*")
    .eq("project_task_id", taskId)
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("That task is not in this organization.");
  return data;
}

/**
 * Load a project's tasks. Online: fetch and refresh the device copy. Offline
 * (or a failed fetch): the device copy, flagged `fromDevice`.
 */
export async function loadProjectTasks(actor, projectId) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_VIEW);
  if (shouldSkipSupabase(actor) || !projectId) return { tasks: [], fromDevice: false };

  if (isOffline()) {
    const cached = readCachedProjectTasks(actor, projectId);
    return { tasks: sortTasks(applyPendingTaskStatus(actor, cached || [])), fromDevice: true, hasDeviceCopy: cached != null };
  }

  try {
    const organizationId = await requireOrg(actor);
    const { data, error } = await supabase
      .from("project_tasks")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("project_id", projectId)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw error;
    const tasks = sortTasks(data || []);
    rememberProjectTasks(actor, projectId, tasks);
    return { tasks, fromDevice: false };
  } catch (error) {
    const cached = readCachedProjectTasks(actor, projectId);
    if (cached) return { tasks: sortTasks(applyPendingTaskStatus(actor, cached)), fromDevice: true, hasDeviceCopy: true };
    throw error;
  }
}

async function insertTask(actor, organizationId, payload) {
  const { data, error } = await supabase
    .from("project_tasks")
    .insert({
      organization_id: organizationId,
      status: TASK_STATUS.OPEN,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
      ...payload,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Create a Project Task from a catalog item (snapshot) or manually.
 *
 *   { catalogItem }                       → snapshot of that catalog item
 *   { title, description }                → one-off task
 *   { title, description, saveToCatalog } → one-off AND a new catalog item
 *                                           (duplicate guarded; admins only)
 */
export async function createProjectTask(actor, projectId, input, { catalog } = {}) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE, {}, undefined, MANAGE_DENIED);
  requireOnline();
  const organizationId = await requireOrg(actor);
  await loadWritableProject(actor, projectId, organizationId);

  const { catalogItem, locationId = null, saveToCatalog = false } = input;
  const featureId = locationId ? input.featureId || null : null;
  await assertFeatureOnLocation(organizationId, projectId, locationId, featureId);
  const base = { projectId, locationId, featureId };
  const extras = {
    priority: input.priority,
    assigneeId: input.assigneeId,
    dueDate: input.dueDate,
    startDate: input.startDate,
    ...(input.durationMinutes !== undefined ? { durationMinutes: input.durationMinutes } : {}),
  };
  let payload;

  if (catalogItem) {
    if (catalogItem.is_active === false) throw new Error("That catalog task is archived.");
    payload = buildTaskFromCatalog(catalogItem, {
      ...base,
      ...extras,
      title: input.title,
      description: input.description,
    });
    const checked = validateTaskInput({
      title: payload.title,
      description: payload.description || "",
      dueDate: payload.due_date || "",
      startDate: payload.start_date || "",
      durationMinutes: payload.planned_duration_minutes ?? null,
    });
    if (!checked.ok) throw new Error(Object.values(checked.errors)[0]);
  } else {
    const result = validateTaskInput(input);
    if (!result.ok) throw new Error(Object.values(result.errors)[0]);
    payload = buildManualTask({ ...base, title: input.title, description: input.description, ...extras });

    if (saveToCatalog) {
      assertCan(actor, CAPABILITY.TASK_CATALOG_EDIT, {}, undefined, "Only an organization admin can save to the Task Catalog.");
      const known = catalog || (await loadTaskCatalog(actor));
      const duplicate = findCatalogDuplicate(known, input.title);
      if (duplicate) {
        // Never silently duplicate: reuse is the caller's choice.
        const error = new Error(
          duplicate.is_active === false
            ? "That title is in the Task Catalog but archived."
            : "That task is already in the Task Catalog.",
        );
        error.code = "CATALOG_DUPLICATE";
        error.existing = duplicate;
        throw error;
      }
      const created = await createCatalogTask(actor, input, { existing: known });
      payload.source_task_catalog_id = created.task_catalog_id;
    }
  }

  const row = await insertTask(actor, organizationId, payload);
  return row;
}

const EDITABLE = [
  "title",
  "description",
  "priority",
  "assignee_id",
  "due_date",
  "location_id",
  "feature_id",
  "start_date",
  "planned_duration_minutes",
];

// Added by 20261021200000. Sent only when set (or being cleared), so edits of
// tasks without a schedule never depend on these columns.
const SCHEDULE_KEYS = ["start_date", "planned_duration_minutes"];

/**
 * A Feature may only be set with its own Location, in this project and
 * organization. The database trigger enforces the same rule; checking here
 * gives a readable error before the write.
 */
async function assertFeatureOnLocation(organizationId, projectId, locationId, featureId) {
  if (!featureId) return;
  if (!locationId) throw new Error("Choose a location before choosing a feature.");
  const { data, error } = await supabase
    .from("project_features")
    .select("project_feature_id, project_id, project_location_id, organization_id, is_active")
    .eq("project_feature_id", featureId)
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) throw error;
  if (
    !data ||
    data.is_active === false ||
    data.project_id !== projectId ||
    data.project_location_id !== locationId ||
    data.organization_id !== organizationId
  ) {
    throw new Error("That feature is not part of the chosen location.");
  }
}

export async function updateProjectTask(actor, taskId, patch) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE, {}, undefined, MANAGE_DENIED);
  requireOnline();
  const organizationId = await requireOrg(actor);
  const row = await loadTaskRow(actor, taskId, organizationId);
  await loadWritableProject(actor, row.project_id, organizationId);

  const clean = {};
  EDITABLE.forEach((key) => {
    if (key in patch) clean[key] = patch[key] === "" ? null : patch[key];
  });
  if ("title" in clean || "description" in clean) {
    const result = validateTaskInput({ title: clean.title ?? row.title, description: clean.description ?? "" });
    if (!result.ok) throw new Error(Object.values(result.errors)[0]);
  }
  if (typeof clean.title === "string") clean.title = clean.title.replace(/\s+/g, " ").trim();
  SCHEDULE_KEYS.forEach((key) => {
    if (key in clean && clean[key] == null && row[key] == null) delete clean[key];
  });
  if ("start_date" in clean || "due_date" in clean || "planned_duration_minutes" in clean) {
    // Start, due and duration are independent: each is validated, none is
    // recalculated from the others.
    const schedule = validateTaskInput({
      title: clean.title ?? row.title,
      dueDate: ("due_date" in clean ? clean.due_date : row.due_date) || "",
      startDate: ("start_date" in clean ? clean.start_date : row.start_date) || "",
      durationMinutes: "planned_duration_minutes" in clean ? clean.planned_duration_minutes : null,
    });
    const scheduleError = schedule.errors.startDate || schedule.errors.dueDate || schedule.errors.duration;
    if (scheduleError) throw new Error(scheduleError);
  }

  // Scope: clearing the location clears the feature; moving to a different
  // location drops a feature that is not on the new one (never an invalid pair).
  if ("location_id" in clean || "feature_id" in clean) {
    const nextLocation = "location_id" in clean ? clean.location_id : row.location_id;
    let nextFeature = "feature_id" in clean ? clean.feature_id : row.feature_id ?? null;
    if (!nextLocation) nextFeature = null;
    else if (!("feature_id" in clean) && nextLocation !== row.location_id) nextFeature = null;
    await assertFeatureOnLocation(organizationId, row.project_id, nextLocation, nextFeature);
    // Only send feature_id when it is (or was) set, so tasks without a feature
    // never depend on the column being present.
    if (nextFeature || row.feature_id) clean.feature_id = nextFeature || null;
    else delete clean.feature_id;
  }

  const { data, error } = await supabase
    .from("project_tasks")
    .update(clean)
    .eq("project_task_id", taskId)
    .eq("organization_id", organizationId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * C8: complete / reopen that also works offline. Online: the normal path. Offline: queued for replay under the
 * user's own session (taskStatusQueue), and the device copy shows the new status as "waiting to sync".
 */
export async function setProjectTaskStatusOfflineAware(actor, task, status) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_COMPLETE, {}, undefined, MANAGE_DENIED);
  if (![TASK_STATUS.OPEN, TASK_STATUS.COMPLETED].includes(status)) {
    throw new Error("Unknown task status.");
  }
  if (isOffline()) {
    return queueTaskStatus(actor, task, status);
  }
  const updated = await setProjectTaskStatus(actor, task.project_task_id, status);
  discardPendingTaskStatus(actor, task.project_task_id);
  return updated;
}

/** Complete / reopen. Any signed-in org member may do this. */
export async function setProjectTaskStatus(actor, taskId, status) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_COMPLETE, {}, undefined, MANAGE_DENIED);
  requireOnline();
  if (![TASK_STATUS.OPEN, TASK_STATUS.COMPLETED].includes(status)) {
    throw new Error("Unknown task status.");
  }
  const organizationId = await requireOrg(actor);
  const row = await loadTaskRow(actor, taskId, organizationId);
  await loadWritableProject(actor, row.project_id, organizationId);

  const { data, error } = await supabase
    .from("project_tasks")
    .update({ status })
    .eq("project_task_id", taskId)
    .eq("organization_id", organizationId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProjectTask(actor, taskId) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE, {}, undefined, MANAGE_DENIED);
  requireOnline();
  const organizationId = await requireOrg(actor);
  const row = await loadTaskRow(actor, taskId, organizationId);
  await loadWritableProject(actor, row.project_id, organizationId);

  const { error } = await supabase
    .from("project_tasks")
    .update({ is_active: false })
    .eq("project_task_id", taskId)
    .eq("organization_id", organizationId)
    .eq("is_active", true);
  if (error) throw error;
}

/** Persist a planTaskMove() result. */
export async function saveTaskOrder(actor, projectId, updates) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE, {}, undefined, MANAGE_DENIED);
  requireOnline();
  if (!updates?.length) return;
  const organizationId = await requireOrg(actor);
  await loadWritableProject(actor, projectId, organizationId);
  for (const update of updates) {
    const { error } = await supabase
      .from("project_tasks")
      .update({ sort_order: update.sort_order })
      .eq("project_task_id", update.project_task_id)
      .eq("organization_id", organizationId)
      .eq("project_id", projectId);
    if (error) throw error;
  }
}
